/**
 * Property module types matching backend DTOs.
 *
 * Sources:
 *   - PropertyEntity, PropertyRequest, PropertyDTO, PropertyPageResponse — property-service
 *   - OrderResponse, Service, PropertyAccess, Appointment, Discount, PaymentMethod — order-service
 *   - PropertyBulkUploadRequest — property-service bulk upload
 *   - OrderFilterMetadataResponse, ServiceSummaryDTO — order-service filters
 */

import type { ComponentData } from './table';

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

// Order matches order-service FileUploadConstants.accessType and the bulk-order
// template dropdown, so enum-derived option lists render in the same order.
export enum AccessType {
  KEYPAD = 'Keypad',
  LOCKBOX = 'Lockbox',
  RENTLY = 'Rently',
  SMART_RENT = 'SmartRent',
  SHOW_MOJO = 'ShowMojo',
  TENANT_TURNER = 'Tenant Turner',
  RENTLY_KEYPAD = 'Rently Keypad',
  RENTLY_SMART_LOCK = 'Rently Smart Lock',
  NO_ACCESS_NEEDED = 'No Access Needed',
}

export enum TableCellType {
  TEXT = 'text',
  SELECT = 'select',
  NUMBER = 'number',
  DATE = 'date',
  ZIP = 'zip',
}

export enum TableStateType {
  ERROR = 'error',
  SUCCESS = 'success',
  DEFAULT = 'default',
  NO_ERROR = 'no-error',
}

// ---------------------------------------------------------------------------
// Property Service DTOs
// ---------------------------------------------------------------------------

/** Full property response from GET /property-service/api/v1/properties */
export interface PropertyEntity {
  propId: string;
  fileId: string | null;
  address1: string;
  unitNumber: string | null;
  city: string;
  state: string;
  zipCode: string;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  propertyType: string;
  // `propertyId` and `unitId` are backend-generated UUIDs — opaque to
  // customers; used internally for routing, cart, sync APIs, etc.
  // `clientPropertyId` and `clientUnitId` are the customer-supplied
  // identifiers from their PMS (e.g. AppFolio/Buildium asset IDs); these
  // are what gets rendered anywhere the UI shows "Property ID" / "Unit ID"
  // to the user. Legacy/migrated rows may have only the UUIDs populated;
  // per PR #944 those rows render an empty cell rather than falling back
  // to the UUID, since the UUID is meaningless to end users.
  propertyId: string | null;
  clientPropertyId: string | null;
  propertyName: string | null;
  propertyStatus: string | null;
  propertySource: string | null;
  portfolioName: string | null;
  propertyGroupName: string | null;
  unitId: string | null;
  clientUnitId: string | null;
  rentReady: boolean | null;
  availableDate: string | null;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  noticeGivenDate: string | null;
  moveInDate: string | null;
  moveOutDate: string | null;
  leaseSignedDate: string | null;
  leaseStatus: string | null;
  listingDate: string | null;
  isListed: boolean | null;
  isActive: boolean | null;
  latitude: string | null;
  longitude: string | null;
  // property-service serializes these Instant fields as epoch SECONDS
  // (e.g. 1780358755.976), not ISO strings. Parse via epochSecondsToDate
  // before formatting. See also lastOrderDate below.
  createdAt: number | null;
  updatedAt: number | null;
  country: string | null;
  rent: number | null;
  leaseId: string | null;
  halfBaths: number | null;
  unitOccupancyStatusID: string | null;
  nextMoveInDate: string | null;
  portfolioID: string | null;
  primaryLeaseStatusID: string | null;
  tenants: string[] | null;
  expectedMonthlyRentAmount: string | null;
  pmsSource: string | null;
  placeId: string | null;
  entity: string | null;
  entityUUID: string | null;
  daysOnMarket: number | null;
  assignee: string | null;
  hasPhotos: boolean | null;
  has3DTour: boolean | null;
  lastOrderDate: string | null;
  orderUUID: string | null;
  orderNumber: string | null;
  services: string[] | null;
  orderStatus: string | null;
  logoUrl: string | null;
  propertyAddress: string | null;
  startDate: string | null;
  hasPhotoset: boolean | null;
}

/** Paginated property list response */
export interface PropertyPageResponse {
  totalElements: number;
  totalElementsAfterFilter: number;
  content: PropertyEntity[];
}

/** Create/Update property request body — matches backend property-service PropertyRequest. */
export interface PropertyRequest {
  propertyType: string;
  address1: string;
  address2?: string | null;
  unitNumber?: string | null;
  city: string;
  state: string;
  zipCode: string;
  bedrooms?: number | null;
  bathrooms?: number | null;
  squareFeet?: number | null;
  clientPropertyId?: string | null;
  clientUnitId?: string | null;
  propertyStatus?: string | null;
  propertySource?: string | null;
  entity?: string | null;
  entityUUID?: string | null;
  portfolioName?: string | null;
  propertyGroupName?: string | null;
  rentReady?: boolean | null;
  availableDate?: string | null;
  leaseStartDate?: string | null;
  leaseEndDate?: string | null;
  noticeGivenDate?: string | null;
  moveInDate?: string | null;
  moveOutDate?: string | null;
  leaseSignedDate?: string | null;
  leaseStatus?: string | null;
  listingDate?: string | null;
  isListed?: boolean | null;
  isOccupied?: boolean;
  isActive?: boolean | null;
  latitude?: string | null;
  longitude?: string | null;
  country?: string | null;
  rent?: number | null;
  leaseId?: string | null;
  halfBaths?: number | null;
  unitOccupancyStatusID?: string | null;
  nextMoveInDate?: string | null;
  portfolioID?: string | null;
  primaryLeaseStatusID?: string | null;
  tenants?: string[] | null;
  expectedMonthlyRentAmount?: string | null;
  pmsSource?: string | null;
}

/** Property DTO for bulk upload rows (includes errorReasons) */
export interface PropertyDTO extends PropertyEntity {
  id: string;
  errorReasons: string[] | null;
  status: string | null;
}

/** Bulk upload file record */
export interface PropertyBulkUploadRequest {
  id: string;
  fileId: string;
  fileName: string;
  uploadedBy: string;
  uploadedDate: string;
  orderPlaced: number | null;
  orderRejected: number | null;
  entityUUID: string | null;
}

/** Entity property count metadata */
export interface PropertyCountDTO {
  entity: string;
  propertyCount: number;
  completedOrder: number;
  activeOrder: number;
  pmsSource: string | null;
  pmsActivationDate: string | null;
}

// ---------------------------------------------------------------------------
// Order Service DTOs
// ---------------------------------------------------------------------------

/** Service line item in an order */
export interface OrderService {
  lineItemUUID: string | null;
  shoppingCartSlug: string;
  quantity: number | null;
  name: string;
  price: number | null;
  discount: number | null;
  occupancyStatus: string | null;
}

/** Property access instructions */
export interface PropertyAccess {
  accessType: string | null;
  accessCode: string | null;
  lockboxLocation: string | null;
  onsiteContactName: string | null;
  onsiteContactPhone: string | null;
  propertyVacant: string | null;
  rentlyRegistrationPin: string | null;
  gateCode: string | null;
  accessLocation: string | null;
}

/** Order appointment */
export interface Appointment {
  appointmentUUID: string | null;
  appointmentId: number | null;
  orderId: string | null;
  startUtc: string | null;
  endUtc: string | null;
  dateStartLocal: string | null;
  timeStartLocal: string | null;
  timeEndLocal: string | null;
  durationMinutes: number | null;
  planotechName: string | null;
  cancelationReason: string | null;
  cancelationNotes: string | null;
  canceled: boolean | null;
}

/** Discount applied to order */
export interface OrderDiscount {
  name: string | null;
  code: string | null;
  discountUUID: string | null;
  discountAmountApply: number | null;
  discountPercentApply: number | null;
}

/** Order-creation snapshot summary (DV-2207). Captures the order as the
    customer placed it. `null` on orders created before snapshotting deployed
    (~May 2026) — those orders cannot show original-service history. */
export interface OrderSnapshotSummary {
  services: OrderService[] | null;
  orderAmount: number | null;
  paymentMethod: PaymentMethod | null;
  propertyAccess: PropertyAccess | null;
}

/** Payment method on order */
export interface PaymentMethod {
  source: string | null;
  sourceTransactionId: string | null;
  sourcePaymentMethodId: string | null;
  sourceUserId: string | null;
  type: string | null;
  amount: number | null;
}

/** Parent order (bulk order wrapper) */
export interface ParentOrder {
  uuid: string;
  parentOrderNumber: string | null;
  fileId: string | null;
  fileName: string | null;
  subTotal: number | null;
  savings: number | null;
  promoCode: string | null;
  promocodeDiscount: number | null;
  totalPrice: number | null;
  createdDate: string | null;
}

/** Order property (nested in OrderResponse) */
export interface OrderPropertyResponse {
  propId: string;
  address1: string;
  unitNumber: string | null;
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  propertyId: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  entity: string | null;
  entityUUID: string | null;
  propertyAddress: string | null;
  logoUrl: string | null;
  hasPhotos: boolean;
  has3DTour: boolean;
  hasPhotoset: boolean | null;
  /** Origin of the property record: 'Sync' (pulled from a PMS) or 'Manual'.
      Gates the asset-sync affordance in the orders table. */
  propertySource: string | null;
  /** PMS the property is connected to (e.g. 'rentvine', 'buildium'). */
  pmsSource: string | null;
  /** Customer's PMS-side identifiers (null on legacy/migrated rows). */
  clientPropertyId: string | null;
  clientUnitId: string | null;
}

/** Full order response from GET /order-service/api/v1/orders */
export interface OrderResponse {
  orderId: string;
  orderNumber: string | null;
  property: OrderPropertyResponse | null;
  propertyId: string | null;
  propertyAddress: string | null;
  propertyPrice: number | null;
  isAssetsSync: boolean | null;
  legacyOrderUUID: string | null;
  orderStatus: string | null;
  orderStatusUpdatedAt: string | null;
  // DV-2357: true when this order's invoice PDF currently exists in S3 (backend
  // existence check). Gates the View/Download invoice controls in the Amount
  // column. Absent on an older backend → treated as false (controls hidden).
  invoiceAvailable?: boolean;
  importName: string | null;
  orderMethod: string | null;
  orderDate: string | null;
  orderedBy: string | null;
  unableToComplete: boolean | null;
  unableToCompleteReason: string | null;
  unableToCompleteNotes: string | null;
  orderCancellationReason: string | null;
  orderCancellationNotes: string | null;
  externalPropertyId: string | null;
  externalOrderId: string | null;
  paymentMethod: PaymentMethod | null;
  cardNumber: string | null;
  cardType: string | null;
  orderCompletedDate: string | null;
  entityUUID: string | null;
  entity: string | null;
  specialInstructions: string | null;
  services: OrderService[] | null;
  discount: OrderDiscount | null;
  propertyAccess: PropertyAccess | null;
  firstAvailableDate: string | null;
  preferredDate: string | null;
  preferredTime: string | null;
  appointments: Appointment[] | null;
  parentOrderId: string | null;
  parentOrder: ParentOrder | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  assetUUID: string | null;
  assetNote: string | null;
  assetUrl: string | null;
  photosetUUID: string | null;
  /** DV-3366: the photoset this order resolves to (order-service asks
   *  asset-service; three legs — the order's own link, a photoset created for
   *  the order, the photoset holding most of its photos). Filled on the orders
   *  list only. Null when nothing resolves OR when that lookup failed — the two
   *  are indistinguishable on the wire (see orderPhotosetTarget). */
  resolvedPhotosetUUID?: string | null;
  /** DV-3366: client visibility of the resolved photoset. Three-valued; null
   *  reads as visible. Unread by the internal entry, where hidden sets show. */
  visibleToClient?: boolean | null;
  createSnapshot: OrderSnapshotSummary | null;
}

/** Paginated order list response (reuses same shape as property pagination) */
export interface OrderPageResponse {
  totalElements: number;
  totalElementsAfterFilter: number;
  content: OrderResponse[];
}

/** Service summary for filter dropdowns */
export interface ServiceSummaryDTO {
  shoppingCartSlug: string;
  name: string;
}

/** Order filter metadata response */
export interface OrderFilterMetadataResponse {
  services: ServiceSummaryDTO[];
  orderedBy: string[];
}

// ---------------------------------------------------------------------------
// UI / Table Types
// ---------------------------------------------------------------------------

/** Filter state for the property/order list view */
export interface TableFilter {
  address: string | null;
  fullAddress: string | null;
  status: string | null;
  orderStatus: string | string[] | null;
  orderDateFrom: string | null;
  orderDateTo: string | null;
  entityUUIDs: string[] | null;
  activeStatus: string | null;
}

/** Processed row for the DataTable (property listing context) */
export interface PropertyTableRow {
  id: string;
  apiResponseRaw: PropertyEntity;
  card: ComponentData;
  propertyAddress: ComponentData;
  lastOrderDate: ComponentData;
  age: ComponentData;
  propertyStatus: ComponentData;
  isActive: string;
  isListed: string;
  daysOnMarket: string;
  assignee: string;
  portfolioName: string;
  hasPhotos: string;
  has3DTour: string;
  noticeGivenDate: string;
  moveOutDate: string;
  propertySource: string;
  leaseStartDate: string;
  leaseEndDate: string;
  rentReady: string | null;
  availableDate: string;
  moreOption: ComponentData;
  listingDate: string;
  propertyType: string;
  tenantStatus: string;
  entity: string;
  formattedAddress: string;
  hasPhotoset: boolean;
}

/** Processed row for the DataTable (order listing context) */
export interface OrderTableRow {
  id: string;
  apiResponseRaw: OrderResponse;
  card: ComponentData;
  propertyAddress: ComponentData;
  orderNumber: ComponentData;
  orderDate: string;
  'appointments.dateStartLocal': string;
  asset: ComponentData;
  orderStatus: ComponentData;
  servicesOrdered: ComponentData;
  orderNotes: ComponentData;
  orderAmount: ComponentData;
  orderedBy: string;
  orderCompletedDate: string;
  paymentMethod: string;
  propertyId: string;
  unitId: string;
  unableToComplete: string;
  unableToCompleteReason: string;
  unableToCompleteNotes: string;
  accessType: string;
  onsiteContactName: string;
  firstAvailableDate: string;
  entity: string;
  gateCode: string;
  lockboxLocation: string;
  moreOption: ComponentData;
  formattedAddress: string;
  hasPhotoset: boolean;
}

// ---------------------------------------------------------------------------
// Form Types
// ---------------------------------------------------------------------------

/** Formik values for the add/edit property form */
export interface PropertyFormValues {
  PropertyType: string;
  address: string;
  other: string;
  state: string;
  city: string;
  zipCode: string;
  bedrooms: string;
  bathrooms: string;
  squareFoot: string;
  clientPropertyId: string;
  propertyStatus: string;
  entity: string;
  entityUUID: string;
  portfolioName: string;
  propertyGroup: string;
  clientUnitId: string;
  rentReady: string | null;
  availableDate: string;
  leaseStartDate: string;
  leaseEndDate: string;
  noticeGivenDate: string;
  moveInDate: string;
  moveOutDate: string;
  leaseSignedDate: string;
  listingDate: string;
  isListed: string | null;
  isOccupied: string;
  isActive: string | null;
  leaseStatus: string | null;
  latitude: string;
  longitude: string;
  country: string;
}

// ---------------------------------------------------------------------------
// Bulk Import Types
// ---------------------------------------------------------------------------

/** Request body for fixing property bulk import errors */
export interface PropertyErrorFormData {
  address1?: string;
  unitNumber?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  bedrooms?: number;
  bathrooms?: number;
  squareFeet?: number;
  propertyType?: string;
  propertyName?: string;
  propertyStatus?: string;
  portfolioName?: string;
  propertyGroupName?: string;
  unitId?: string;
  rentReady?: boolean;
  availableDate?: string;
  leaseStartDate?: string;
  leaseEndDate?: string;
  noticeGivenDate?: string;
  moveInDate?: string;
  moveOutDate?: string;
  leaseSignedDate?: string;
  leaseStatus?: string;
  listingDate?: string;
  isListed?: boolean;
  isOccupied?: boolean;
  isActive?: boolean;
  entity?: string;
  latitude?: string;
  longitude?: string;
}

/** Request body for fixing order bulk import errors */
export interface OrderErrorFormData {
  propertyUuid?: string;
  userId?: string;
  services?: string[];
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  isOnsiteAppointment?: boolean;
  onsiteContactName?: string;
  onsiteContactPhone?: string;
  isAccessNeeded?: boolean;
  accessType?: string;
  lockboxLocation?: string;
  accessCode?: string;
  rentlyRegistrationPin?: string;
  entity?: string;
  gateCode?: string;
  specialInstructions?: string;
  preferredAppointmentDate?: string;
  propertyId?: string;
  propertyType?: string;
  beds?: number;
  baths?: number;
  squareFeet?: number;
}

/** Response from fixing order bulk import errors */
export interface OrderBulkFixResponse {
  orderApproved: number;
  orderRejected: number;
  fileId: string;
  subTotal: number;
}

/** Bulk file upload response (from initial upload) */
export interface BulkUploadResponse {
  fileId: string;
  fileName: string;
  fileNumber: number;
  orderRejected: number;
  orderApproved: number;
  subTotal: number;
}

/** Editable error cell value in the error details table */
export interface ErrorCellValue {
  value: string | null;
  isActive: boolean;
  errorMessage: string;
  isOptionalField: boolean;
}

/** Editable error row model for the error details table */
export interface ErrorFormModel {
  // Shared identifiers
  fileId?: string;
  propId?: string;
  entityUUID?: string;

  // Property fields
  address1?: ErrorCellValue;
  unitNumber?: ErrorCellValue;
  city?: ErrorCellValue;
  state?: ErrorCellValue;
  zipCode?: ErrorCellValue;
  propertyType?: ErrorCellValue;
  bedrooms?: ErrorCellValue;
  bathrooms?: ErrorCellValue;
  squareFeet?: ErrorCellValue;
  unitId?: ErrorCellValue;
  portfolioName?: ErrorCellValue;
  propertyStatus?: ErrorCellValue;
  rentReady?: ErrorCellValue;
  listingDate?: ErrorCellValue;
  isListed?: ErrorCellValue;
  daysOnMarket?: ErrorCellValue;
  availableDate?: ErrorCellValue;
  leaseSignedDate?: ErrorCellValue;
  leaseStartDate?: ErrorCellValue;
  leaseEndDate?: ErrorCellValue;
  moveInDate?: ErrorCellValue;
  noticeGivenDate?: ErrorCellValue;
  moveOutDate?: ErrorCellValue;
  assignee?: ErrorCellValue;
  isActive?: ErrorCellValue;

  // Order fields
  services?: ErrorCellValue;
  address?: ErrorCellValue;
  isOnsiteAppointment?: boolean;
  onsiteContactName?: ErrorCellValue;
  onsiteContactPhone?: ErrorCellValue;
  isAccessNeeded?: boolean;
  accessType?: ErrorCellValue;
  lockboxLocation?: ErrorCellValue;
  accessCode?: ErrorCellValue;
  rentlyRegistrationPin?: ErrorCellValue;
  entity?: ErrorCellValue;
  gateCode?: ErrorCellValue;
  specialInstructions?: ErrorCellValue;
  preferredAppointmentDate?: ErrorCellValue;
  firstAvailableDate?: ErrorCellValue;
  orderNotes?: ErrorCellValue;
  propertyId?: ErrorCellValue;
  beds?: ErrorCellValue;
  baths?: ErrorCellValue;
}

/** Props for the EditableCell component */
export interface EditableCellProps {
  value: string | null;
  id: number;
  isActive: boolean;
  options?: Array<{ value: string; label: string }>;
  type: TableCellType;
  isDisabled: boolean;
  errorMessage: string;
  setData: (id: number, field: string, value: string) => void;
  name: string;
  isOptionalField: boolean;
  isDecimal?: boolean;
  maxCount?: number;
  setMaxMin?: boolean;
  isOnlyText?: boolean;
}

/** Status indicator for import history rows */
export interface TableState {
  type: TableStateType;
  text: string;
  onClicked?: () => void;
}

// ---------------------------------------------------------------------------
// Filter Metadata
// ---------------------------------------------------------------------------

/** More filters panel props */
export interface AdvancedFilterProps {
  isProperty: boolean;
  hideModal: () => void;
  homeFilter: TableFilter;
  onApplyFilters: (moreAPIFilters: string) => void;
  onResetFilters: () => void;
  currentFilterQuery: string;
  setFiltersCount: (count: number) => void;
  paramOrderId: string;
  setShowClearFilterBtn: (show: boolean) => void;
}

// ---------------------------------------------------------------------------
// Permission Types
// ---------------------------------------------------------------------------

export interface PropertyPermissions {
  canGet: boolean;
  canPost: boolean;
  canPut: boolean;
  canDelete: boolean;
}

// ---------------------------------------------------------------------------
// Legacy Form Data — `usePropertyForm` + `propertyFormUtils.buildPropertyRequest`
// ---------------------------------------------------------------------------

/** @deprecated Use `PropertyFormValues` instead. Kept for the add-property
 * form's submit pipeline; transformed into `PropertyRequest` before the
 * `useCreateProperty` / `useUpdateProperty` mutation. */
export type AddPropertiesFormData = {
  PropertyType?: string;
  address: string;
  other?: string;
  state: string;
  city: string;
  zipCode: string;
  bedrooms?: number;
  bathrooms?: number;
  squareFoot?: string;
  rent?: string;
  clientPropertyId?: string;
  propertyStatus?: string;
  entity?: string;
  entityUUID?: string;
  portfolioName?: string;
  propertyGroup?: string;
  clientUnitId?: string;
  rentReady?: string | null;
  availableDate?: string;
  leaseStartDate?: string;
  leaseEndDate?: string;
  noticeGivenDate?: string;
  country?: string;
  moveInDate?: string;
  moveOutDate?: string;
  leaseSignedDate?: string;
  listingDate?: string;
  isListed?: string | null;
  isOccupied?: string;
  isActive?: string | null;
  leaseStatus?: string | null;
  latitude?: string;
  longitude?: string;
};
